// apiClient.ts — Factory for authenticated fetch helpers.
// Encapsulates the pattern duplicated across cooking/objectifio/authz frontends:
// read a JWT from localStorage under a configurable key, attach it as a
// Bearer token, and forward credentials for cookie-based sessions.
//
// Also provides peekAuthFailure + invalidateSession so any SPA can react to
// a 401/403 from an authenticated request by printing *why* it failed (the
// reason returned by the lib/oidcrp middleware: "authentication required",
// "invalid or expired token", "missing scope: X") before clearing local
// token state and bouncing the user back through the login flow.

export interface ApiClientConfig {
    // localStorage key that holds the JWT for this app. Apps use distinct
    // keys so multiple sibling deployments on the same origin can't stomp
    // each other's sessions.
    tokenKey: string;
    // Optional base URL prefix (defaults to same-origin).
    baseUrl?: string;
    // Invoked when a request through this client returns 401 or 403. Use
    // it to globally surface auth failures (default: log + clearToken +
    // redirect — see invalidateSession). Apps that want per-call handling
    // should leave this unset and inspect responses themselves.
    onAuthInvalid?: (info: AuthFailureInfo) => void;
}

export interface ApiClient {
    fetch: (path: string, options?: RequestInit) => Promise<Response>;
    getToken: () => string | null;
    setToken: (token: string) => void;
    clearToken: () => void;
}

// AuthFailureInfo describes a 401/403 from an authenticated request. The
// reason field carries the human-readable message from the server's auth
// middleware (`{"error":"..."}`); the original Response is included so
// callers can read the body themselves if they need more detail.
export interface AuthFailureInfo {
    reason: string;
    status: number;
    path: string;
    response: Response;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
    const { tokenKey, baseUrl = '', onAuthInvalid } = config;

    const getToken = (): string | null => {
        try {
            return localStorage.getItem(tokenKey);
        } catch {
            return null;
        }
    };

    const setToken = (token: string): void => {
        try {
            localStorage.setItem(tokenKey, token);
        } catch {
            // Swallow — storage may be disabled in private mode.
        }
    };

    const clearToken = (): void => {
        try {
            localStorage.removeItem(tokenKey);
        } catch {
            // Swallow.
        }
    };

    const apiFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
        const token = getToken();
        const headers: Record<string, string> = {
            ...(options.headers as Record<string, string> | undefined ?? {}),
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const url = baseUrl ? `${baseUrl}${path}` : path;
        const response = await fetch(url, { ...options, headers, credentials: 'include' });

        if ((response.status === 401 || response.status === 403) && onAuthInvalid) {
            // Don't fire the callback for the /api/me probe that useAuth does
            // on first paint with no token — that's the normal unauthenticated
            // bootstrap, not an invalidation event.
            if (token || hasSessionCookie()) {
                const reason = await peekAuthFailure(response, `HTTP ${response.status}`);
                onAuthInvalid({ reason, status: response.status, path, response });
            }
        }
        return response;
    };

    return { fetch: apiFetch, getToken, setToken, clearToken };
}

// peekAuthFailure extracts the human-readable error message from a 401/403
// response without consuming the body — the original Response stays
// readable. Falls back to `fallback` when the body isn't JSON or doesn't
// carry an `error`/`message` key.
export async function peekAuthFailure(res: Response, fallback = 'authentication invalid'): Promise<string> {
    try {
        const ct = res.headers.get('content-type') ?? '';
        if (ct.includes('application/json')) {
            const data = await res.clone().json() as { error?: string; message?: string };
            return data.error ?? data.message ?? fallback;
        }
        const text = (await res.clone().text()).trim();
        return text || fallback;
    } catch {
        return fallback;
    }
}

// invalidateSession is the canonical reaction to an auth failure: print
// the *reason* to the console so the developer (and an attentive user
// reading devtools) knows why they got kicked out, drop the local token,
// hit the configured logout path so the server clears its session cookie,
// and bounce the user back through the login flow.
//
// It is idempotent: if the page is already at `redirectTo`, no redirect
// fires (avoids a loop when the login page itself probes /api/me).
export function invalidateSession(
    client: ApiClient,
    reason: string,
    opts: { logoutPath?: string; redirectTo?: string } = {},
): void {
    const redirectTo = opts.redirectTo ?? '/login';
    console.error(
        `[authz] session invalidated: ${reason}. Clearing local token; ` +
        `bouncing to ${redirectTo} to re-authenticate.`,
    );
    client.clearToken();
    if (opts.logoutPath && typeof fetch !== 'undefined') {
        // Fire-and-forget; the redirect tears the page down before this
        // resolves anyway. credentials:'include' makes sure the Set-Cookie
        // clearing the session cookie reaches the browser.
        try {
            void fetch(opts.logoutPath, { method: 'POST', credentials: 'include' });
        } catch {
            // sendBeacon would be more robust but isn't always available.
        }
    }
    if (typeof window !== 'undefined' && window.location.pathname !== redirectTo) {
        window.location.replace(redirectTo);
    }
}

// hasSessionCookie returns true if the browser appears to carry a
// session-style cookie (anything that ends in `_auth`). Used by the
// apiClient to avoid firing onAuthInvalid on the bootstrap /api/me probe
// when the user simply isn't signed in yet.
function hasSessionCookie(): boolean {
    if (typeof document === 'undefined' || !document.cookie) return false;
    return document.cookie.split(';').some((c) => c.trim().split('=')[0].endsWith('_auth'));
}

// Convenience: parse a Response as JSON, throw on non-2xx with the server's
// error message when available. Apps call this after a successful fetch so
// each page doesn't reimplement the "unwrap JSON or throw" pattern.
export async function unwrapJson<T = unknown>(res: Response): Promise<T> {
    const contentType = res.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        if (isJson) {
            try {
                const body = await res.json() as { error?: string; message?: string };
                msg = body.error ?? body.message ?? msg;
            } catch {
                // Fallthrough with default msg.
            }
        }
        throw new Error(msg);
    }
    if (!isJson) {
        // Callers that expect no body can pass T = void.
        return undefined as T;
    }
    return res.json() as Promise<T>;
}
